import * as React from "react";
import { AIAgentTask, AiScriptChangeIteration } from "../../../../../generated/api";
import { Header, Icon, Label, Popup, SemanticICONS, SemanticCOLORS } from "semantic-ui-react";

const statItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    marginRight: '18px',
    marginBottom: '10px',
    fontSize: '0.95em',
    lineHeight: '1.5',
};
const statItemIconStyle: React.CSSProperties = { marginRight: '7px' };
const statItemLabelStyle: React.CSSProperties = { fontWeight: 'bold', marginRight: '6px' };

export interface TaskStatsModalProps {
    task: AIAgentTask;
}

export function TaskStatsModal(props: TaskStatsModalProps) {
    const { task } = props;
    const startTime = new Date(task.createdAt);

    let determinedEndTime: Date | null = null;
    let taskDisplayStatusLabel: string = "Unknown";
    let taskDisplayStatusColor: SemanticCOLORS = "grey";

    if (task.iterations && task.iterations.length > 0) {
        const lastIteration = task.iterations[task.iterations.length - 1];
        if (lastIteration.status === "DONE") {
            taskDisplayStatusLabel = "Done";
            taskDisplayStatusColor = "green";
            determinedEndTime = task.taskCompletionTime
                ? new Date(task.taskCompletionTime)
                : new Date(lastIteration.timestamp);
        } else if (lastIteration.status === "ABORTED") {
            taskDisplayStatusLabel = "Aborted";
            taskDisplayStatusColor = "red";
            determinedEndTime = new Date(lastIteration.timestamp);
        }
    }

    if (!determinedEndTime) {
        if (task.taskCompletionTime) {
            taskDisplayStatusLabel = "Done";
            taskDisplayStatusColor = "green";
            determinedEndTime = new Date(task.taskCompletionTime);
        } else {
            determinedEndTime = new Date(task.createdAt);
            if (taskDisplayStatusLabel === "Unknown") {
                taskDisplayStatusLabel = "Error";
                taskDisplayStatusColor = "orange";
            }
        }
    }

    let llmDurationMs = 0;
    let totalPrice = 0;
    let totalPromptTokens = 0;
    let totalCompletionTokens = 0;

    if (task.iterations) {
        for (const iteration of task.iterations) {
            llmDurationMs += iteration.durationMs || 0;
            const modelData = iteration.aiModelResponseScripting;
            if (modelData) {
                totalPrice += modelData.totalPrice || 0;
                totalPromptTokens += modelData.totalUsageTokensNumber || 0;
                totalCompletionTokens += modelData.totalCompletionTokensNumber || 0;
            }
        }
    }

    const totalDurationMs = determinedEndTime ? determinedEndTime.getTime() - startTime.getTime() : null;
    const userTimeMs =
        totalDurationMs !== null && llmDurationMs !== null
            ? totalDurationMs - llmDurationMs
            : null;

    const overallTotalTokens = totalPromptTokens + totalCompletionTokens;
    const modelName = task.aiModelEnum?.toString() || "N/A";

    const formatDuration = (ms: number | null) => {
        if (ms === null) return "N/A";
        if (ms < 0) return "Error";
        if (ms === 0) return "0s";
        let seconds = Math.floor(ms / 1000);
        let minutes = Math.floor(seconds / 60);
        let hours = Math.floor(minutes / 60);
        seconds = seconds % 60;
        minutes = minutes % 60;
        let str = "";
        if (hours > 0) str += `${hours}h `;
        if (minutes > 0) str += `${minutes}m `;
        if (seconds > 0 || (hours === 0 && minutes === 0)) str += `${seconds}s`;
        return str.trim() || "0s";
    };

    const formatDateTime = (date: Date | null, type: 'time' | 'date') => {
        if (!date) return "N/A";
        if (type === 'time') return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
    };

    const formatPrice = (price: number) => {
        if (price === 0) return "$0.00";
        if (Math.abs(price) >= 1) return `$${price.toFixed(2)}`;
        return `$${price.toFixed(4)}`;
    };

    const formatTokens = (tokens: number) => tokens.toLocaleString();

    const StatItem: React.FC<{
        iconName: SemanticICONS;
        iconColor?: SemanticCOLORS;
        value: string;
        popupContent: string;
        label?: string;
    }> = ({ iconName, iconColor = 'grey', value, popupContent, label }) => (
        <Popup
            trigger={
                <div style={statItemStyle}> {/* Uses statItemStyle */}
                    <Icon name={iconName} color={iconColor} style={statItemIconStyle} size="large" /> {/* Uses statItemIconStyle */}
                    {label && <span style={statItemLabelStyle}>{label}:</span>} {/* Uses statItemLabelStyle */}
                    <span>{value}</span>
                </div>
            }
            content={popupContent}
            position="top center"
            size="tiny"
            inverted
        />
    );

    return (
        <div style={{ marginTop: '1rem', padding: '12px 18px', border: '1px solid #e0e0e0', borderRadius: '4px', backgroundColor: '#fdfdfd' }}>
            <Header as="h5" style={{ margin: '0 0 12px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '1.1em' }}>
                <span><Icon name="chart bar" color="blue" /> Task Statistics</span>
                <Label color={taskDisplayStatusColor} size="small" style={{fontWeight: 'normal'}}>{taskDisplayStatusLabel}</Label>
            </Header>

            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                <StatItem iconName="calendar alternate outline" value={`${formatDateTime(startTime, 'time')} (${formatDateTime(startTime, 'date')})`} popupContent={`Started: ${startTime.toLocaleString()}`} label="Start"/>
                {determinedEndTime ? (
                    <StatItem
                        iconName={taskDisplayStatusLabel === "Aborted" ? "calendar times outline" : "calendar check outline"}
                        iconColor={taskDisplayStatusColor}
                        value={`${formatDateTime(determinedEndTime, 'time')} (${formatDateTime(determinedEndTime, 'date')})`}
                        popupContent={`${taskDisplayStatusLabel}: ${determinedEndTime.toLocaleString()}`}
                        label={taskDisplayStatusLabel === "Aborted" ? "Aborted at" : "End"}
                    />
                ) : (
                    <div style={statItemStyle}>
                        <Icon name="question circle" color="grey" style={statItemIconStyle} size="large" />
                        <span style={statItemLabelStyle}>End:</span>
                        <span>Unknown</span>
                    </div>
                )}
                <StatItem iconName="clock outline" iconColor="blue" value={formatDuration(totalDurationMs)} popupContent={`Total Task Duration: ${formatDuration(totalDurationMs) || 'N/A'}`} label="Total Duration"/>
                <StatItem iconName="cogs" iconColor="teal" value={formatDuration(llmDurationMs)} popupContent={`Total LLM Processing + Computing Time: ${formatDuration(llmDurationMs) || 'N/A'}`} label="Computing Duration"/>
                {userTimeMs !== null && userTimeMs >= 0 && (
                    <StatItem iconName="clock" iconColor="orange" value={formatDuration(userTimeMs)} popupContent={`User/Wait Time: ${formatDuration(userTimeMs) || 'N/A'}`} label="Wait & User Time"/>
                )}
                <StatItem iconName="microchip" iconColor="purple" value={modelName} popupContent={`AI Model: ${modelName}`} label="Model"/>
                <StatItem iconName="file alternate outline" iconColor="violet" value={formatTokens(totalCompletionTokens)} popupContent={`Total Completion Tokens: ${formatTokens(totalCompletionTokens)}`} label="Completion Tokens"/>
                <StatItem iconName="file text outline" iconColor="pink" value={formatTokens(totalPromptTokens)} popupContent={`Total Prompt Tokens: ${formatTokens(totalPromptTokens)}`} label="Usage Token"/>
                <StatItem iconName="boxes" iconColor="brown" value={formatTokens(overallTotalTokens)} popupContent={`Overall Total Tokens: ${formatTokens(overallTotalTokens)}`} label="Total Token"/>
                <StatItem iconName="dollar sign" iconColor="olive" value={formatPrice(totalPrice)} popupContent={`Total Estimated Cost: ${formatPrice(totalPrice)}`} label="AI-Cost"/>
            </div>
        </div>
    );
}